import { ReactNode, useEffect } from 'react';
import { PersonalAccountOpenProfileDetailsPages, usePersonalAccountOpenProfileDetailsPageTypeStore } from 'views/pages/accountOpenProfileDetails/personalProfileDetails/hooks';
import { usePersonalAccountEstablishStore } from '../hooks';
import { AuthModeSelectType, Gender, ImportantPublicWorkerType } from '../customerInfo/hooks';
import {
  ChangeAccountInfoMutationVariables,
  OpenAccountMutationVariables,
  useChangeAccountInfoMutation,
  useOpenAccountMutation,
} from 'views/utils/graphql/graphql';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import {
  findOccupationId,
  findDomesticRealEstatePurposeId,
  findPersonalBusinessContentId,
  ChangeAccountInfoExecuteType,
  findInvestorCategoryInfoId,
  InvestorCategoryInfo,
  FatcaStatus,
} from 'views/utils/enums';
import { DateFormatType, formatDate } from 'views/utils/converter';
import { TemplateProps } from '../common';
import { PageStep } from 'views/pages/accountInfo/hooks';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useFormik } from 'formik';
import { AccountType } from 'views/templates/module/bankDetailsInput/hooks';
import { CountryOfResidence, Nationality } from '../advanceQuestion/hooks';

export type DisplayItemProps = {
  label: string;
  value: string | ReactNode;
  hidden?: boolean;
};

type EntryConfirmType = {
  agreeToPersonalAccountOpenProfileDetails: boolean;
};

export type EntryConfirmProps = {
  isMailChanged?: (mailAddress: string) => boolean | undefined;
} & TemplateProps;

export const useEntryConfirm = ({ isUpdate, setPageStep, isMailChanged }: EntryConfirmProps) => {
  const { param, add } = usePersonalAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = usePersonalAccountOpenProfileDetailsPageTypeStore();
  const [_openAccountResponse, openAccount] = useOpenAccountMutation();
  const [_changeAccountResponse, changeAccount] = useChangeAccountInfoMutation();
  const { showError } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const initialValues: EntryConfirmType = {
    agreeToPersonalAccountOpenProfileDetails: false,
  };

  const formik = useFormik<EntryConfirmType>({
    enableReinitialize: isUpdate,
    initialValues,
    async onSubmit() {
      add({
        ...param,
        ...formik.values,
      });
      setAccountOpenProfileDetailsPageType(PersonalAccountOpenProfileDetailsPages.entryConfirm);
    },
    // validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const agreeToPersonalAccountOpenProfileDetailsProps = {
    checked: formik.values.agreeToPersonalAccountOpenProfileDetails,
    onChange: () => formik.setFieldValue('agreeToPersonalAccountOpenProfileDetails', !formik.values.agreeToPersonalAccountOpenProfileDetails),
    error: formik.errors.agreeToPersonalAccountOpenProfileDetails,
  };

  const execute = {
    onClick: async () => {
      if (isUpdate) {
        if (isMailChanged && isMailChanged(param.customerMailAddress)) {
          await updateMailCheck();
        } else {
          await executeUpdateAccount();
        }
      } else {
        await executeOpenAccount();
      }
    },
  };

  const updateMailCheck = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        setPageStep && setPageStep(PageStep.SENDMAIL);
      },
    },
    [showError],
  );

  const executeOpenAccount = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const data: OpenAccountMutationVariables = {
          input: {
            PersonalAccount: {
              UniqueID: param.uniqueId,
              Nationality: param.nationality ? Nationality.Japan : Nationality.Other,
              CountryOfResidence: param.countryOfResidence ? CountryOfResidence.Yes : CountryOfResidence.No,
              Birthdate: formatDate(param.birthdate, DateFormatType.Slash_yyyyMMdd),
              HaveEKYCDocument: param.identificationQuestion,
              CanTakePicture: param.selfieQuestion,
              ConfirmResponsibility: param.confirmResponsibility,
              DealerFirstName: param.customerFirstName,
              DealerLastName: param.customerLastName,
              DealerFirstNameKana: param.customerFirstNameKana,
              DealerLastNameKana: param.customerLastNameKana,
              Sex: param.gender,
              PostCode: param.customerPostCode,
              Prefecture: param.customerPrefecture,
              CityCountry: param.customerCityCountry,
              TownStreetAddress: param.customerTownStreetAddress,
              BuildingNameAndRoomNumber: param.customerBuildingNameAndRoomNumber,
              DealerMobilePhoneNumber: param.customerMobilePhone,
              ContactPhoneNumber: param.customerHomePhone,
              ContactFaxNumber: param.customerFaxNumber,
              DealerMailAddress: param.customerMailAddress,
              Occupation: param.occupation.filter((x) => x.checked).map((x) => findOccupationId(x.label) ?? null),
              OccupationOtherText: param.occupationOtherText,
              PersonalMainBusinessContent: findPersonalBusinessContentId(param.customerMainBusinessContent),
              PersonalMainBusinessContentOtherText: param.customerMainBusinessContentOtherText,
              PersonalOtherBusinessContent: findPersonalBusinessContentId(param.customerOtherBusinessContent),
              PersonalOtherBusinessContentOtherText: param.customerOtherBusinessContentOtherText,
              DomesticRealEstatePurpose: param.purpose
                .filter((x) => x.checked)
                .map((x) => findDomesticRealEstatePurposeId(x.label) ?? null),
              DomesticRealEstatePurposeOtherText: param.purposeOtherText,
              Fatca: param.fatca,
              ImportantPublicWorker: param.importantPublicWorker,
              TransferBankName: param.bankName,
              TransferBranchName: param.branchName,
              TransferAccountType: param.accountType,
              TransferAccountNumber: param.accountNumber,
              TransferAccountHolderName: param.accountHolderName,
              InvoiceNumber: param.invoiceNumber,
              HasNoInvoiceNumber: param.hasNoInvoiceNumber,
              ElectronicDocumentAgreement: param.electronicDocumentAgreement,
              ElectronicDocumentAgreementDateTime: param.electronicDocumentAgreementDateTime,
              TwoFactorAuthenticationType: param.authMode,
            },
          },
        };
        const openAccountData = await openAccount(data);
        if (!openAccountData?.data) return;

        if (openAccountData.data.OpenAccount?.Code === 0) {
          setAccountOpenProfileDetailsPageType(PersonalAccountOpenProfileDetailsPages.entryComplete);
        } else {
          showError(openAccountData.data.OpenAccount?.Message);
        }
      },
    },
    [showError],
  );

  const executeUpdateAccount = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const data: ChangeAccountInfoMutationVariables = {
          input: {
            Name: `${param.customerLastName}　${param.customerFirstName}`,
            Furigana: `${param.customerLastNameKana}　${param.customerFirstNameKana}`,
            HaveEKYCDocument: param.identificationQuestion,
            CanTakePicture: param.selfieQuestion,
            ConfirmResponsibility: param.confirmResponsibility,
            DealerFirstName: param.customerFirstName,
            DealerLastName: param.customerLastName,
            DealerFirstNameKana: param.customerFirstNameKana,
            DealerLastNameKana: param.customerLastNameKana,
            PostCode: param.customerPostCode,
            Prefecture: param.customerPrefecture,
            CityCountry: param.customerCityCountry,
            TownStreetAddress: param.customerTownStreetAddress,
            BuildingNameAndRoomNumber: param.customerBuildingNameAndRoomNumber,
            DealerMobilePhoneNumber: param.customerMobilePhone,
            ContactPhoneNumber: param.customerHomePhone,
            ContactFaxNumber: param.customerFaxNumber,
            DealerMailAddress: param.customerMailAddress,
            Occupation: param.occupation.filter((x) => x.checked).map((x) => findOccupationId(x.label) ?? null),
            OccupationOtherText: param.occupationOtherText,
            PersonalMainBusinessContent: findPersonalBusinessContentId(param.customerMainBusinessContent),
            PersonalMainBusinessContentOtherText: param.customerMainBusinessContentOtherText,
            PersonalOtherBusinessContent: findPersonalBusinessContentId(param.customerOtherBusinessContent),
            PersonalOtherBusinessContentOtherText: param.customerOtherBusinessContentOtherText,
            DomesticRealEstatePurpose: param.purpose
              .filter((x) => x.checked)
              .map((x) => findDomesticRealEstatePurposeId(x.label) ?? null),
            DomesticRealEstatePurposeOtherText: param.purposeOtherText,
            Fatca: param.fatca,
            ImportantPublicWorker: param.importantPublicWorker,
            TransferBankName: param.bankName,
            TransferBranchName: param.branchName,
            TransferAccountType: param.accountType,
            TransferAccountNumber: param.accountNumber,
            TransferAccountHolderName: param.accountHolderName,
            InvoiceNumber: param.invoiceNumber,
            HasNoInvoiceNumber: param.hasNoInvoiceNumber,
            TwoFactorAuthenticationType: param.authMode,
            //個人口座は固定で上記のいずれでもないが入っていそうなので入れる
            InvestorCategoryInfo: [findInvestorCategoryInfoId(InvestorCategoryInfo.Other)!],
          },
        };

        const changeAccountData = await changeAccount(data);
        if (!changeAccountData?.data) return;

        if (changeAccountData.data.ChangeAccountInfo?.Code === 0) {
          setPageStep && setPageStep(PageStep.COMPLETE);
        } else {
          showError(changeAccountData.data.ChangeAccountInfo?.Message);
        }
      },
    },
    [showError],
  );

  const goBack = {
    onClick: () => {
      if (isUpdate) {
        setPageStep && setPageStep(PageStep.UPDATE);
      } else {
        setAccountOpenProfileDetailsPageType(PersonalAccountOpenProfileDetailsPages.customerInfo);
      }
    },
  };

  const displayInfo = {
    corporateInfo: [
      {
        label: '氏名',
        value: `${param.customerLastName} ${param.customerFirstName}(${param.customerLastNameKana} ${param.customerFirstNameKana})`,
      },
      ...(!isUpdate ? [{
        label: '国籍',
        value: `${param.nationality ? '日本' : ''}`,
      },
      {
        label: '居住地',
        value: `${param.nationality ? '日本のみ' : '日本以外'}`,
      },
      {
        label: '生年月日',
        value: formatDate(param.birthdate, DateFormatType.Slash_yyyyMMdd),
      },
      {
        label: '性別',
        value: getGenderText(param.gender),
      },] : []),
      {
        label: '住所',
        value: `${param.customerPostCode} ${param.customerPrefecture}${param.customerCityCountry}${param.customerTownStreetAddress}${param.customerBuildingNameAndRoomNumber}`,
      },
      {
        label: '電話番号',
        value: param.customerMobilePhone,
      },
      {
        label: '固定電話',
        value: param.customerHomePhone,
      },
      {
        label: 'FAX番号',
        value: param.customerFaxNumber,
      },
      {
        label: 'メールアドレス',
        value: param.customerMailAddress,
      },
      {
        label: '職業',
        value:
          param.occupation
            .filter((ck) => ck.checked)
            .map((ck) => ck.label)
            .join('／') + (param.occupationOtherText ? `（${param.occupationOtherText}）` : ''),
      },
      {
        label: '事業内容',
        value: param.customerMainBusinessContent
          ? `${
              param.customerMainBusinessContent +
              `${param.customerMainBusinessContentOtherText ? `(${param.customerMainBusinessContentOtherText})` : ''}`
            }${param.customerOtherBusinessContent ? '／' : ''}${
              param.customerOtherBusinessContent +
              `${param.customerOtherBusinessContentOtherText ? `(${param.customerOtherBusinessContentOtherText})` : ''}`
            }`
          : '',
      },
      {
        label: 'お取引の目的',
        value:
          param.purpose
            .filter((ck) => ck.checked)
            .map((ck) => ck.label)
            .join('／') + (param.purposeOtherText ? `（${param.purposeOtherText}）` : ''),
      },
      //居住地国
      {
        label: '米国納税義務（FATCA）に関する確認',
        value: getFatcaText(param.fatca),
      },
      {
        label: '外国の重要な公人への該当性',
        value: getImportantPublicWorkerText(param.importantPublicWorker),
      },
      {
        label: '配当金振込用口座',
        value: `${param.bankName}／${param.branchName}／${getAccountType(param.accountType)}／${param.accountNumber}／${
          param.accountHolderName
        }`,
      },
      {
        label: 'インボイス番号',
        value: `${param.hasNoInvoiceNumber ? 'なし' : param.invoiceNumber}`,
      },
      {
        label: '2段階認証の方法',
        value: getTwoAuthenticationText(param.authMode),
      },
    ] as DisplayItemProps[],
  };

  return { agreeToPersonalAccountOpenProfileDetailsProps, execute, goBack, displayInfo };
};

const getTwoAuthenticationText = (type: AuthModeSelectType) => {
  switch (type) {
    case AuthModeSelectType.SMS:
      return 'SMSによる認証';
    case AuthModeSelectType.MailAddress:
      return 'メールによる認証';
    case AuthModeSelectType.None:
    default:
      return '';
  }
};

const getGenderText = (type: Gender) => {
  switch (type) {
    case Gender.Male:
      return '男性';
    case Gender.Female:
      return '女性';
    case Gender.PreferNotToSay:
      return '回答しない';
    default:
      return '';
  }
};

const getAccountType = (type: AccountType) => {
  switch (type) {
    case AccountType.Ordinary:
      return '普通';
    case AccountType.Checking:
      return '当座';
    case AccountType.Savings:
      return '貯蓄';
    case AccountType.Other:
      return 'その他';
    case AccountType.None:
    default:
      return '';
  }
};

const getImportantPublicWorkerText = (type: ImportantPublicWorkerType) => {
  switch (type) {
    case ImportantPublicWorkerType.Yes:
      return '外国の重要な公人もしくはその親族に該当します';
    case ImportantPublicWorkerType.No:
      return '外国の重要な公人もしくはその親族には該当しません';
    default:
      return '';
  }
};
const getFatcaText = (type: FatcaStatus) => {
  switch (type) {
    case FatcaStatus.Yes:
      return '私は米国市民（米国籍保有者）または米国居住者です';
    case FatcaStatus.No:
      return '私は米国市民（米国籍保有者）または米国居住者ではありません';
    default:
      return '';
  }
};
